import { UnityEngineAppConfig } from '../config';
import { WorkoutRuntimeOptions } from '../options';
import { UnityEngineExerciseSettings } from '../settings';
import * as ActionTypes from './actionTypes';

/**
 * Unity exercise external callbacks
 */
// appReady callback
export const unityAppReady: () => ActionTypes.IUnityAppReadyAction = () => ({
  type: ActionTypes.UNITY_APP_READY,
});

// engineReady callback
export const unityEngineReady: () => ActionTypes.IUnityEngineReadyAction = () => ({
  type: ActionTypes.UNITY_ENGINE_READY,
});

// exerciseReady callback
export const unityExerciseReady: () => ActionTypes.IUnityExerciseReadyAction = () => ({
  type: ActionTypes.UNITY_EXERCISE_READY,
});

// completeExercise callback
export const unityExerciseComplete: (result: string) => ActionTypes.IUnityExerciseCompleteAction = (
  result: string
) => ({
  type: ActionTypes.UNITY_EXERCISE_COMPLETE,
  payload: result,
});

// exerciseFailed callback
export const unityExerciseFailed: (message: string) => ActionTypes.IUnityExerciseFailedAction = (message: string) => ({
  type: ActionTypes.UNITY_EXERCISE_FAILED,
  payload: message,
});

// updates progress value from UnityLoader
export const unityProgressUpdate: (progress: number) => ActionTypes.IUnityProgressUpdateAction = (
  progress: number
) => ({
  type: ActionTypes.UNITY_PROGRESS_UPDATE,
  payload: progress,
});

/**
 * gameplay sound updated callback
 */
export const unityGameplaySoundSettingsUpdated: (
  result: number
) => ActionTypes.IUnityGameplaySoundSettingsUpdatedAction = (result: number) => ({
  type: ActionTypes.UNITY_GAMEPLAY_SOUND_SETTINGS_UPDATED,
  payload: result,
});

/**
 * gameplay background updated callback
 */
export const unityGameplayBackgroundSettingsUpdated: (
  result: number
) => ActionTypes.IUnityGameplayBackgroundSettingsUpdatedAction = (result: number) => ({
  type: ActionTypes.UNITY_GAMEPLAY_BACKGROUND_SETTINGS_UPDATED,
  payload: result,
});

/**
 * gameplay fullscreen updated callback
 */
export const unityGameplayFullscreenSettingsUpdated: (
  result: number
) => ActionTypes.IUnityGameplayFullscreenSettingsUpdatedAction = (result: number) => ({
  type: ActionTypes.UNITY_GAMEPLAY_FULLSCREEN_SETTINGS_UPDATED,
  payload: result,
});

// trackEvent callback
export const unityTrackEvent: (eventData: string) => ActionTypes.IUnityTrackEventAction = (eventData) => ({
  type: ActionTypes.UNITY_TRACK_EVENT,
  payload: eventData,
});

export const unityUpdateStats: (statId: string, payload: string) => ActionTypes.IUnityStatsUpdateAction = (
  statId,
  payload
) => ({
  type: ActionTypes.UNITY_STATS_UPDATE,
  statId,
  payload,
});

/**
 * custom execution flow actions
 */

// inject unity loader into document
export const unityInit: (
  basePath: string,
  canvasId: string,
  fullscreenClass?: string,
  dataUrl?: string,
  codeUrl?: string,
  frameworkUrl?: string
) => ActionTypes.IUnityInitAction = (basePath, canvasId, fullscreenClass, dataUrl, codeUrl, frameworkUrl) => ({
  type: ActionTypes.UNITY_INIT,
  payload: {
    basePath,
    canvasId,
    fullscreenClass,
    dataUrl,
    codeUrl,
    frameworkUrl,
  },
});

// unity loader starts to load
export const unityLoaderStart: () => ActionTypes.IUnityLoaderStartAction = () => ({
  type: ActionTypes.UNITY_LOADER_START,
});

// initialize app
export const unityAppInit: (config: UnityEngineAppConfig) => ActionTypes.IUnityAppInitAction = (config) => ({
  type: ActionTypes.UNITY_APP_INIT,
  payload: config,
});

// initialize exercise
export const unityExerciseInit: (settings: UnityEngineExerciseSettings) => ActionTypes.IUnityExerciseInitAction = (
  settings
) => ({
  type: ActionTypes.UNITY_EXERCISE_INIT,
  payload: settings,
});

// start exercise
export const unityExerciseStart: (options: WorkoutRuntimeOptions) => ActionTypes.IUnityExerciseStartAction = (
  options: WorkoutRuntimeOptions
) => ({
  type: ActionTypes.UNITY_EXERCISE_START,
  payload: options,
});

// exercise is running
export const unityExerciseRunning: () => ActionTypes.IUnityExerciseRunningAction = () => ({
  type: ActionTypes.UNITY_EXERCISE_RUNNING,
});

// exercise is running
export const unityRemoveExercise: () => ActionTypes.IUnityExerciseRemoveAction = () => ({
  type: ActionTypes.UNITY_EXERCISE_REMOVE,
});

// stop everything
export const unityStop: () => ActionTypes.IUnityStopAction = () => ({
  type: ActionTypes.UNITY_STOP,
});

/**
 * debugging features
 */

// log a message
export const unityLog: (message: string) => ActionTypes.IUnityLogAction = (message: string) => ({
  type: ActionTypes.UNITY_LOG,
  payload: message,
});

// log an error
export const unityError: (message: string) => ActionTypes.IUnityErrorAction = (message: string) => ({
  type: ActionTypes.UNITY_ERROR,
  payload: message,
});

// show an alert
export const unityAlert: (message: string) => ActionTypes.IUnityAlertAction = (message: string) => ({
  type: ActionTypes.UNITY_ALERT,
  payload: message,
});

// set loading flag to true
export const unityLoadingStart: () => ActionTypes.IUnityLoadingStartAction = () => ({
  type: ActionTypes.UNITY_LOADING_START,
});

// set loading flag to false
export const unityLoadingEnd: () => ActionTypes.IUnityLoadingEndAction = () => ({
  type: ActionTypes.UNITY_LOADING_END,
});
